<template>
  <div class="m-guided-checkout-bar">
    <div class="m-guided-checkout-bar__card">
      <div class="m-guided-checkout-bar__inner">
        <div
          v-if="thumbnail"
          class="m-guided-checkout-bar__thumb"
        >
          <img
            :src="thumbnail"
            :alt="productName"
            width="64"
            height="64"
          >
        </div>

        <div class="m-guided-checkout-bar__summary">
          <strong>{{ productName }}</strong>
          <span v-if="areaLabel">{{ areaLabel }}</span>
        </div>

        <div class="m-guided-checkout-bar__breakdown">
          <ul
            v-if="visibleLines.length"
            class="m-guided-checkout-bar__lines"
          >
            <li
              v-for="line in visibleLines"
              :key="line.id"
              class="m-guided-checkout-bar__line"
            >
              <a
                v-if="line.product"
                href="#"
                class="m-guided-checkout-bar__line-label m-guided-checkout-bar__line-label--link"
                :title="line.label"
                @click.prevent="openProductDetails(line.product)"
              >
                {{ line.label }}
              </a>
              <span
                v-else
                class="m-guided-checkout-bar__line-label"
                :title="line.label"
              >{{ line.label }}</span>
              <span class="m-guided-checkout-bar__line-value">{{ formatLineValue(line) }}</span>
            </li>
          </ul>
        </div>

        <div class="m-guided-checkout-bar__price">
          <div class="m-guided-checkout-bar__price-row">
            <span class="m-guided-checkout-bar__price-label">Gesamtpreis</span>
            <SfPrice
              class="m-guided-checkout-bar__price-value"
              :regular="totalPrice"
            />
          </div>
          <div class="taxes">
            Inkl. 19% MwSt., zzgl.
            <router-link :to="localizedRoute('/versandkosten')">
              Versand
            </router-link>
          </div>
        </div>

        <div class="m-guided-checkout-bar__action">
          <div
            v-if="!isPickup"
            class="add-to-cart__online"
          >
            <SfButton
              class="sf-button a-add-to-cart sf-button--full-width btn btn-primary sf-add-to-cart__button m-guided-checkout-bar__cta m-guided-checkout-bar__cta--online"
              :class="{ 'm-guided-checkout-bar__cta--blocked': isBlocked }"
              :disabled="isAddingToCart"
              :aria-disabled="isBlocked"
              @click="onCtaClick"
            >
              Online bestellen
            </SfButton>

            <div class="product-addtocart-bottom">
              <OnlineAvailability
                :product="product"
                :qty="stock.max"
                :threshold-qty="stock.thresholdQuantity"
                :show-delivery-time="true"
              />
            </div>
          </div>

          <div
            v-else
            class="add-to-cart__click-and-collect"
          >
            <SfButton
              class="sf-button a-add-to-cart sf-button--full-width click-and-collect__button btn btn-primary m-guided-checkout-bar__cta m-guided-checkout-bar__cta--pickup"
              :class="{ 'm-guided-checkout-bar__cta--blocked': isBlocked }"
              :disabled="isAddingToCart"
              :aria-disabled="isBlocked"
              @click="onCtaClick"
            >
              Abholen
            </SfButton>

            <div class="product-addtocart-bottom">
              <StoreAvailability
                :product="product"
                :qty="stock.max"
                :qty-pickup="stock.maxPickup"
                :threshold-qty="stock.thresholdQuantity"
                :show-modal-button="true"
                :show-not-selected="true"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SfPrice, SfButton } from '@storefront-ui/vue'
import { price } from '@vue-storefront/core/filters/price'
import { formatProductMessages } from '@vue-storefront/core/filters/product-messages'
import { notifications } from '@vue-storefront/core/modules/cart/helpers'
import OnlineAvailability from 'src/modules/vsf-inventory-sources/components/OnlineAvailability'
import StoreAvailability from 'src/modules/vsf-inventory-sources/components/StoreAvailability'
import { mapGetters, mapActions } from 'vuex'
import { ModalList } from 'theme/store/ui/modals'

export default {
  name: 'MGuidedCheckoutBar',
  inject: {
    prepareGuidedCheckoutForCart: {
      default: null
    }
  },
  components: {
    SfPrice,
    SfButton,
    OnlineAvailability,
    StoreAvailability
  },
  props: {
    product: {
      type: Object,
      required: true
    },
    productName: {
      type: String,
      default: ''
    },
    areaLabel: {
      type: String,
      default: ''
    },
    totalPrice: {
      type: [Number, String],
      default: null
    },
    priceLines: {
      type: Array,
      default: () => []
    },
    qty: {
      type: Number,
      default: 1
    },
    stock: {
      type: Object,
      default: () => ({})
    },
    onlineDisabled: {
      type: Boolean,
      default: false
    },
    pickupDisabled: {
      type: Boolean,
      default: false
    },
    isPickup: {
      type: Boolean,
      default: false
    },
    thumbnail: {
      type: String,
      default: ''
    },
    accessoryItems: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    ...mapGetters({
      isAddingToCart: 'cart/getIsAdding'
    }),
    visibleLines () {
      // Fulfillment/shipping is surfaced under the buttons, so drop it from the line items.
      return this.priceLines.filter(line => line.id !== 'delivery')
    },
    isBlocked () {
      if (formatProductMessages(this.product.errors) !== '') return true
      return this.isPickup ? this.pickupDisabled : this.onlineDisabled
    }
  },
  methods: {
    ...mapActions('ui', {
      openMicrocart: 'toggleMicrocart',
      openModal: 'openModal'
    }),
    formatLineValue (line) {
      if (line.display) return line.display
      if (line.amount == null) return '—'
      return price(line.amount)
    },
    openProductDetails (product) {
      if (!product) return
      this.openModal({ name: ModalList.DataSheet, payload: product })
    },
    onCtaClick () {
      if (this.isAddingToCart) return
      if (this.isBlocked) {
        this.$emit('blocked')
        return
      }
      this.addSelectionToCart(this.isPickup)
    },
    async addSelectionToCart (isPickup) {
      await this.$store.dispatch('inventorySources/setIsPickup', isPickup)

      try {
        let product = this.product
        if (this.prepareGuidedCheckoutForCart) {
          product = await this.prepareGuidedCheckoutForCart()
        }

        const diffLog = await this.$store.dispatch('cart/addItem', {
          productToAdd: Object.assign({}, product, { qty: this.qty })
        })

        for (const item of this.accessoryItems) {
          if (!item || !item.product || !item.qty) continue
          await this.$store.dispatch('cart/addItem', {
            productToAdd: Object.assign({}, item.product, { qty: item.qty })
          })
        }

        this.openMicrocart()

        const clientNotifications = diffLog && Array.isArray(diffLog.clientNotifications)
          ? diffLog.clientNotifications
          : []
        clientNotifications.forEach(notificationData => {
          this.$store.dispatch(
            'notification/spawnNotification',
            notificationData,
            { root: true }
          )
        })
      } catch (message) {
        this.$store.dispatch(
          'notification/spawnNotification',
          notifications.createNotification({ type: 'danger', message }),
          { root: true }
        )
      }
    }
  }
}
</script>
